<template>
    <mini-calculator-page>
        <div slot="summary-panel">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Loss of Earnings Calculator" icon="earnings"/>

                <control label="Date of Birth">
                    <date-input v-model="dateOfBirth"></date-input>
                </control>

                <control label="Gender">
                    <select ref="gender" v-model="gender">
                        <option :value="null">Please select...</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                </control>

                <control label="Employment Type">
                    <select ref="employmentType" v-model="employmentType">
                        <option :value="null">Please select...</option>
                        <option value="employed">Employed</option>
                        <option value="self_employed">Self-employed</option>
                    </select>
                </control>

                <control label="Employment Income">
                    <div class="a-form-inputs--half-calcs">
                        <div class="a-form-inputs__container a-form-inputs--pound">
                            <input autocomplete="off" v-model="employmentIncome"
                                   ref="employmentIncome"
                                   v-on:focus="employmentIncome = employmentIncome || ''"
                                   v-on:blur="employmentIncome = employmentIncome || 0"
                                   v-two-dp
                                   type="number">
                        </div>
                        <div class="a-form-inputs__container">
                            <select ref="employmentNetGross" v-model="employmentNetGross">
                                <option value="gross">Gross</option>
                                <option value="net">Net</option>
                            </select>
                        </div>
                    </div>
                </control>

                <p>
                    <income-period
                            v-for="(period, index) in getPeriods"
                            :index="index"
                            :period="period"
                            @update="calculateLoss">
                    </income-period>

                    <button class="a-button a-button--smaller" @click="addNewPeriod()">Add Period</button>
                </p>

                <h2 class="u-r-margin-top-40">
                    Result
                </h2>

                <control label="Total Net Loss" before-after="pound-on-dark">
                    <input autocomplete="off" :value="totalNetLoss" v-two-dp disabled type="number"/>
                </control>
            </div>
        </div>

        <div slot="results-panel">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto p-earnings__calc">
                <h2>
                    Explanation
                </h2>

                <p>
                    Based on a <strong>{{ gender }}</strong> with the Date of Birth (<strong>{{ dateOfBirth }}</strong>)
                    with assumed State Retirement Date of (<strong>{{ retirementDate }}</strong>). The Pre Loss of Earnings Net
                    is <strong>{{ currencyFormat(totalNetIncome) }}</strong>.
                </p>

                <h2>
                    Calculations
                </h2>

                <div :class="{ 'a-loader--table' : loading }"></div>
                <div class="a-table a-table--short-no-style a-table--full-width" v-if="!loading">
                    <table cellspacing="0" cellpadding="0" class="a-table--full-width u-margin-top-20" v-for="(period, index) in getPeriods">
                        <tbody>
                            <tr>
                                <th width="33%">Period</th>
                                <td>{{ index + 1 }}</td>
                            </tr>
                            <tr>
                                <th>From</th>
                                <td>{{ period.startOfPeriod }}</td>
                            </tr>
                            <tr>
                                <th>To</th>
                                <td>{{ period.endOfPeriod }}</td>
                            </tr>
                            <tr>
                                <th>Duration (years)</th>
                                <td>{{ periodDuration(period).toFixed(2) }}</td>
                            </tr>
                            <tr>
                                <th>Pre-Loss<br>Annual Income</th>
                                <td>{{ currencyFormat(totalNetIncome) }}</td>
                            </tr>
                            <tr>
                                <th>Loss of Earnings<br>Annual Income</th>
                                <td>{{ currencyFormat(period.totalNetIncome) }}</td>
                            </tr>
                            <tr>
                                <th>Formula</th>
                                <td>({{ currencyFormat(totalNetIncome) }} x {{ periodDuration(period).toFixed(2) }}) -
                                    ({{ currencyFormat(period.totalNetIncome) }} x {{ periodDuration(period).toFixed(2) }}) </td>
                            </tr>
                            <tr>
                                <th>Period Net Loss</th>
                                <td>{{ currencyFormat(periodNetLoss(period, index)) }}</td>
                            </tr>
                        </tbody>
                    </table>

                    <table cellspacing="0" cellpadding="0" class="a-table--full-width u-margin-top-20">
                        <tfoot>
                            <tr class="a-table--grey">
                                <td class="u-text-right u-bold" width="33%">
                                    Total Net Loss
                                </td>
                                <td>
                                    <strong>
                                        {{ currencyFormat(totalNetLoss) }}
                                    </strong>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import {mapGetters, mapActions} from 'vuex';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import miniCalcIcons from 'components/Icons.vue';
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import DateInput from 'components/form/control/DateInput.vue';
    import currencyFormat from 'mixins/currencyFormat';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';
    import IncomePeriod from 'pages/mini-calculators/components/IncomePeriod';

    export default {
        name: 'loss-of-earnings',
        created() {
            this.resetForm();
        },
        computed: {
            ...mapGetters('mini/lossOfEarnings', {
                findValue: 'findValue',
                getPeriods: 'periods',
                periodsValid: 'periodsValid',
            }),
            dateOfBirth: {
                get() {
                    return this.findValue('dateOfBirth');
                },
                set(value) {
                    this.updateValue({key: 'dateOfBirth', value});
                }
            },
            gender: {
                get() {
                    return this.findValue('gender');
                },
                set(value) {
                    this.updateValue({key: 'gender', value});
                }
            },
            employmentType: {
                get() {
                    return this.findValue('employmentType');
                },
                set(value) {
                    this.updateValue({key: 'employmentType', value});
                }
            },
            employmentIncome: {
                get() {
                    return this.findValue('employmentIncome');
                },
                set(value) {
                    this.updateValue({key: 'employmentIncome', value});
                }
            },
            employmentNetGross: {
                get() {
                    return this.findValue('employmentNetGross');
                },
                set(value) {
                    this.updateValue({key: 'employmentNetGross', value});
                }
            },
            retirementDate: {
                get() {
                    return this.findValue('retirementDate');
                },
            },
            totalNetIncome: {
                get() {
                    return this.findValue('totalNetIncome');
                },
            },
            loading: {
                get() {
                    return this.findValue('loading');
                },
            },
            totalNetLoss: {
                get() {
                    return this.findValue('totalNetLoss');
                },
            },
            isValid() {
                return (
                    this.dateOfBirth !== null &&
                    this.dateOfBirth !== "" &&
                    this.gender !== null &&
                    this.gender !== "" &&
                    this.employmentType !== null &&
                    this.employmentIncome !== null &&
                    this.employmentIncome !== "" &&
                    this.employmentIncome > 0 &&
                    this.periodsValid
                );
            },
        },
        mixins: [
            FormValidator,
            currencyFormat,
            CalculatorKeyBindings
        ],
        components: {
            Control,
            ControlError,
            DateInput,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            miniCalcIcons,
            Intro,
            IncomePeriod
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        methods: {
            ...mapActions('mini/lossOfEarnings', {
                calculatePeriodLoss: 'calculatePeriodLoss',
                updateValue: 'updateValue',
                addPeriod: 'addPeriod',
                resetForm: 'reset',
            }),
            addNewPeriod() {
                this.addPeriod();
                this.calculateLoss()
            },
            periodDuration(period) {
                return moment(period.endOfPeriod, 'DD/MM/YYYY')
                    .diff(moment(period.startOfPeriod, 'DD/MM/YYYY'), 'years', true)
            },
            periodNetLoss(period) {
                let duration = this.periodDuration(period);
                let preLossIncome = this.totalNetIncome;
                let lossIncome = period.totalNetIncome;

                return (parseFloat(duration.toFixed(2)) * preLossIncome)
                    - (parseFloat(duration.toFixed(2)) * lossIncome);
            },
            calculateLoss(payload) {
                if (!this.isValid) {
                    this.showPrompt();
                    this.showResults = false;
                    return;
                }

                this.calculatePeriodLoss(payload.index);
                this.hidePrompt();
                this.showResults = true;
            },
        }
    }
</script>



// WEBPACK FOOTER //
// lossOfEarnings.vue?5f4f1268